import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const LINKEDIN_URL = "https://www.linkedin.com/in/jimcanfield3";
const AMAZON_URL =
  "https://www.amazon.com/CEO-Tools-2-0-System-Manage/dp/0997521074/";

export default function About() {
  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#2D2926]">
      <Navbar />

      {/* HERO */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-[#9A938A] mb-4">
          About Colleague
        </p>
        <h1
          data-testid="about-title"
          className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2D2926]"
        >
          Colleague was built by Jim Canfield after 10,000 hours of coaching
          CEOs&hellip; and one nagging piece of research.
        </h1>
      </section>

      {/* BODY — one flowing narrative, per Jim's latest edit */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-6">
        <div className="space-y-6 text-[17px] leading-[1.75] text-[#3D3833]">
          <p>
            Over the last two decades I&apos;ve had the privilege of working
            closely with more than 300 CEOs and their leadership teams. Some
            days that looked like helping a founder work out whether to fire a
            close friend. Some days it looked like a full-day workshop with a
            room of executives working to see their business more clearly.
            Through the process, CEOs went from clients to friends. Every one
            of those coaching conversations kept pointing me back to the same
            piece of research.
          </p>

          <p>
            <strong>The Gallup question that started this.</strong> For
            decades, Gallup has measured employee engagement with a set of 12
            questions. It is Question 10 that is the one business leaders
            always want to skip:
          </p>

          <blockquote
            data-testid="about-q10"
            className="font-serif text-2xl sm:text-3xl leading-snug text-[#2D2926] border-l-4 border-[#D4A574] pl-6 italic"
          >
            &ldquo;I have a best friend at work.&rdquo;
          </blockquote>

          <p>
            They call it &ldquo;too personal&rdquo; or &ldquo;irrelevant to
            business.&rdquo; Gallup calls it the most predictive item in the
            entire survey. Employees who strongly agree with this question are{" "}
            <strong>7× more likely to be fully engaged.</strong> For the ones
            who don&apos;t have that person, the odds of being engaged drop to{" "}
            <strong>1 in 12.</strong> Only <strong>2 in 10</strong> people
            ever get to say &ldquo;yes&rdquo; to Q10. The rest go to work
            every day carrying more alone than they should: a bad meeting
            they can&apos;t decompress from, a hard decision no one outside
            their role truly understands, a moment of doubt with no one to
            bounce it off. This is exactly what Colleague is trying to help
            with.
          </p>

          <p>
            <strong>A body of work behind the app.</strong> I have founded,
            built or led five companies. My workshop <em>CEO Tools 2.0</em>{" "}
            has been delivered more than 500 times in six countries. My book
            of the same name,{" "}
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-book-link"
              className="text-[#2D2926] hover:text-[#C29260] underline underline-offset-4"
            >
              <em>CEO Tools 2.0: A System to Think, Manage, and Lead like a CEO</em>
            </a>
            , distills the frameworks I&apos;ve used with over 300 CEOs. My
            newest workshop,{" "}
            <em>
              The Value Creation Formula: How to Build the Business Everyone
              Wants to Buy… And You May Not Want to Sell
            </em>
            , is helping company owners build businesses that are more
            scalable, more valuable, and easier to run. Colleague is the
            natural next step, the same frameworks available on demand, on
            the days they matter most.
          </p>
        </div>
      </section>

      {/* KIND WORDS */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2926] mb-6">
          A few kind words along the way.
        </h2>
        <p className="text-[17px] leading-[1.75] text-[#3D3833]">
          Three times my peers at Vistage Worldwide named me Speaker of the
          Year in Canada, the UK, and Worldwide. I served as Chief Learning
          Officer at Vistage, where I recruited and trained 500+ CEO group
          leaders and developed content for more than 11,000 CEO and Key
          Executive members. I&apos;ve had the joy of building programs and
          content alongside people like Tony Hsieh, Chip Conley, Marshall
          Goldsmith, and Jack Stack.
        </p>
      </section>

      {/* WHY AI */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2926] mb-6">
          Why an AI app, and why now.
        </h2>
        <p className="text-[17px] leading-[1.75] text-[#3D3833]">
          I know what Colleague isn&apos;t. It&apos;s not your therapist, your
          HR department, or your lawyer and it&apos;s not meant to be.
          It&apos;s not even a real best friend at work, no software is. But
          on the day you&apos;d otherwise have no one to think out loud with,
          a colleague designed by someone who has spent 10,000 hours in
          coaching rooms is better than nothing. This is my attempt to make
          that kind of coaching available to everyone, on the days it matters
          most.
        </p>
      </section>

      {/* SIGN-OFF */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="rounded-3xl bg-[#FBF6EE] border border-[#EAE3D2] px-6 sm:px-10 py-10 sm:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-8 items-center">
            <img
              src="/jim-800.jpg"
              alt="Jim Canfield"
              width="160"
              height="160"
              loading="lazy"
              className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-full ring-4 ring-white shadow-xl mx-auto sm:mx-0"
            />
            <div>
              <p className="font-serif text-3xl text-[#2D2926]">Jim Canfield</p>
              <p className="mt-1 text-[15px] text-[#6B655D]">
                Author,{" "}
                <em>
                  <a
                    href={AMAZON_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#C29260] underline underline-offset-4"
                  >
                    CEO Tools 2.0
                  </a>
                </em>
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={AMAZON_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-amazon-btn"
                  className="inline-flex items-center gap-1.5 text-sm bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 transition-colors"
                >
                  Get the book on Amazon
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-linkedin-btn"
                  className="inline-flex items-center gap-1.5 text-sm bg-white border border-[#E6E2D8] text-[#2D2926] hover:border-[#D4A574] rounded-full px-5 py-2.5 transition-colors"
                >
                  <Linkedin size={14} strokeWidth={1.5} />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/register"
            data-testid="about-cta-signup"
            className="inline-flex items-center gap-1.5 text-sm bg-[#D4A574] text-[#2D2926] hover:bg-[#C99560] rounded-full px-6 py-3 font-medium transition-colors"
          >
            Build your Colleague — free for 30 days
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
